import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { SupabaseClient, User } from '@supabase/supabase-js';
import {
  createMemoryApi,
  createSupabaseApi,
  createSupabaseClient,
  type PlatHuntApi,
} from '@plathunt/api';
import { accountState, guestState, psnLinkedState } from '@plathunt/auth';
import { capabilitiesFor, type AuthCapabilities, type AuthState } from '@plathunt/types';
import { supabaseConfig, supabaseConfigured } from './env.js';

const ApiContext = createContext<PlatHuntApi | null>(null);

/** 'loading' solange die bestehende Session geprueft wird – erst danach rendert die App. */
export type AuthStatus = 'loading' | 'ready';

export interface AuthResult {
  ok: boolean;
  message: string | null;
}

interface AuthContextValue {
  status: AuthStatus;
  state: AuthState;
  capabilities: AuthCapabilities;
  /** E-Mail der Supabase-Sitzung. Das Profil traegt sie nicht. */
  email: string | null;
  /** Ohne Supabase-Konfiguration laeuft die App auf DEV FIXTURES – dann gibt es keine Anmeldung. */
  authAvailable: boolean;
  signIn: (email: string, password: string) => Promise<AuthResult>;
  signUp: (email: string, password: string) => Promise<AuthResult>;
  signOut: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<AuthResult>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Supabase-Fehlertexte sind englisch und technisch. Hier das, was der Nutzer braucht. */
function readableError(raw: string): string {
  const message = raw.toLowerCase();
  if (message.includes('invalid login credentials')) {
    return 'E-Mail oder Passwort stimmt nicht.';
  }
  if (message.includes('email not confirmed')) {
    return 'Die E-Mail ist noch nicht bestätigt. Bitte den Bestätigungslink aus der Mail öffnen.';
  }
  if (message.includes('already registered') || message.includes('already been registered')) {
    return 'Für diese E-Mail gibt es schon ein Konto. Bitte anmelden.';
  }
  if (message.includes('password') && message.includes('least')) {
    return 'Das Passwort ist zu kurz. Mindestens 8 Zeichen.';
  }
  if (message.includes('rate limit') || message.includes('too many')) {
    return 'Zu viele Versuche. Bitte einen Moment warten.';
  }
  if (message.includes('failed to fetch') || message.includes('network')) {
    return 'Keine Verbindung zum Server. Internetverbindung prüfen.';
  }
  return raw;
}

export function AppProviders({ children }: { children: ReactNode }) {
  const supabase = useMemo<SupabaseClient | null>(
    () => (supabaseConfigured ? createSupabaseClient(supabaseConfig) : null),
    [],
  );
  const api = useMemo<PlatHuntApi>(
    () => (supabase ? createSupabaseApi(supabase) : createMemoryApi()),
    [supabase],
  );

  // Gast ist der Startzustand. Es gibt keinen blockierenden Login.
  const [state, setState] = useState<AuthState>(guestState);
  const [email, setEmail] = useState<string | null>(null);
  const [status, setStatus] = useState<AuthStatus>(supabase ? 'loading' : 'ready');
  const mounted = useRef(true);

  /**
   * Aus der Supabase-Sitzung den PlatHunt-Zustand bauen.
   * Account und PSN bleiben getrennt: ohne verknuepftes PSN-Konto bleibt es
   * bei 'account'.
   */
  const applyUser = useCallback(
    async (user: User | null) => {
      if (!user) {
        setState(guestState);
        setEmail(null);
        return;
      }
      setEmail(user.email ?? null);

      try {
        const profile = await api.users.getProfile(user.id);
        if (!mounted.current) return;
        if (!profile) {
          // Die Sitzung steht, die Profilzeile fehlt (Trigger noch nicht gelaufen).
          setState(guestState);
          return;
        }
        const psnAccount = await api.users.getPsnAccount(user.id).catch(() => null);
        if (!mounted.current) return;
        setState(psnAccount ? psnLinkedState(profile, psnAccount) : accountState(profile));
      } catch {
        // Profil nicht lesbar: lieber Gast als ein halb aufgebauter Zustand.
        if (mounted.current) setState(guestState);
      }
    },
    [api],
  );

  useEffect(() => {
    mounted.current = true;
    if (!supabase) return;

    void supabase.auth
      .getSession()
      .then(({ data }) => applyUser(data.session?.user ?? null))
      .finally(() => {
        if (mounted.current) setStatus('ready');
      });

    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      void applyUser(session?.user ?? null);
    });

    return () => {
      mounted.current = false;
      data.subscription.unsubscribe();
    };
  }, [supabase, applyUser]);

  const signIn = useCallback(
    async (mail: string, password: string): Promise<AuthResult> => {
      if (!supabase) return { ok: false, message: 'Supabase ist nicht konfiguriert.' };
      const { error } = await supabase.auth.signInWithPassword({ email: mail, password });
      return error
        ? { ok: false, message: readableError(error.message) }
        : { ok: true, message: null };
    },
    [supabase],
  );

  const signUp = useCallback(
    async (mail: string, password: string): Promise<AuthResult> => {
      if (!supabase) return { ok: false, message: 'Supabase ist nicht konfiguriert.' };
      const { data, error } = await supabase.auth.signUp({ email: mail, password });
      if (error) return { ok: false, message: readableError(error.message) };
      // Ohne Session ist die E-Mail-Bestaetigung aktiv.
      return {
        ok: true,
        message: data.session
          ? null
          : 'Konto angelegt. Bitte den Bestätigungslink in der E-Mail öffnen.',
      };
    },
    [supabase],
  );

  const signOut = useCallback(async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    setState(guestState);
    setEmail(null);
  }, [supabase]);

  const sendPasswordReset = useCallback(
    async (mail: string): Promise<AuthResult> => {
      if (!supabase) return { ok: false, message: 'Supabase ist nicht konfiguriert.' };
      const { error } = await supabase.auth.resetPasswordForEmail(mail, {
        redirectTo: `${window.location.origin}/login`,
      });
      return error
        ? { ok: false, message: readableError(error.message) }
        : { ok: true, message: null };
    },
    [supabase],
  );

  const auth = useMemo<AuthContextValue>(
    () => ({
      status,
      state,
      capabilities: capabilitiesFor(state),
      email,
      authAvailable: supabase !== null,
      signIn,
      signUp,
      signOut,
      sendPasswordReset,
    }),
    [status, state, email, supabase, signIn, signUp, signOut, sendPasswordReset],
  );

  return (
    <ApiContext.Provider value={api}>
      <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>
    </ApiContext.Provider>
  );
}

export function useApi(): PlatHuntApi {
  const api = useContext(ApiContext);
  if (!api) throw new Error('useApi ausserhalb von AppProviders benutzt.');
  return api;
}

export function useAuth(): AuthContextValue {
  const auth = useContext(AuthContext);
  if (!auth) throw new Error('useAuth ausserhalb von AppProviders benutzt.');
  return auth;
}
